import React, { useState } from "react";
import {
  Box,
  TextField,
  Button,
  Chip,
  Stack,
  Typography,
  Paper,
  CircularProgress,
  Divider,
} from "@mui/material";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import CloseIcon from "@mui/icons-material/Close";
import { apiUrl } from "../config/api";
import { getCurrentLang } from "./LangSelector";
import TranslatedJobCard from "./TranslatedJobCard";
import { useNavigate } from "react-router-dom";
import {
  getAiSearchState,
  setAiSearchState,
  clearAiSearchState,
  setAiSearchScroll,
} from "./aiSearchStore";

/**
 * Умный поиск с ИИ (как в BuyPool, вариант text-to-filter).
 * Фраза → облачный DeepSeek → безопасные фильтры → карточки из базы.
 * Состояние живёт в модуле-синглтоне aiSearchStore, поэтому переживает переходы
 * на /job/:id и обратно (useState сбрасывался бы при размонтировании).
 */
const AiSearch = ({ initialQuery = "" }) => {
  const navigate = useNavigate();
  const _store = getAiSearchState();
  const [query, setQuery] = useState(_store.query || initialQuery);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [res, setRes] = useState(_store.res);

  const run = async (qOverride) => {
    const q = (qOverride !== undefined ? qOverride : query).trim();
    if (!q || busy) return;
    if (qOverride !== undefined) setQuery(qOverride);
    setBusy(true);
    setErr(null);
    try {
      const lang = getCurrentLang();
      const r = await fetch(apiUrl("ai_search.php"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ query: q, lang }),
      });
      const data = await r.json();
      if (!data.success) {
        setErr(data.error || "Ошибка ИИ-поиска");
        setRes(null);
      } else {
        setRes(data);
        setAiSearchState(q, data);
      }
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Ошибка ИИ-поиска");
      setRes(null);
    } finally {
      setBusy(false);
    }
  };

  const used = res?.used;
  const chips = [];
  if (used?.category) chips.push({ label: used.category, cls: "brand" });
  if (used?.city) chips.push({ label: `📍 ${used.city}`, cls: "neutral" });
  if (used?.keywords) used.keywords.forEach((k) => chips.push({ label: k, cls: "neutral" }));
  if (used?.min_salary_usd != null) chips.push({ label: `от ~${Math.round(used.min_salary_usd)}$`, cls: "neutral" });
  if (used?.max_salary_usd != null) chips.push({ label: `до ~${Math.round(used.max_salary_usd)}$`, cls: "neutral" });
  if (used?.salary_required) chips.push({ label: "с указанной зп", cls: "brand" });
  if (used?.exclude) used.exclude.forEach((k) => chips.push({ label: `без: ${k}`, cls: "brand" }));
  if (used?.employment_type) chips.push({ label: used.employment_type, cls: "neutral" });
  if (used?.experience_level) chips.push({ label: used.experience_level, cls: "neutral" });
  if (used?.remote_allowed) chips.push({ label: "Удалённо", cls: "brand" });

  const examples = [
    "водитель такси",
    "php программист с указанной зарплатой",
    "удалённый программист до 2000$",
    "продавец консультант в Бельцах",
    "бухгалтер с опытом от 2 лет",
  ];

  return (
    <Box>
      {/* Поле ввода: на мобильном кнопка уходит вниз, на широких — в один ряд */}
      <Paper
        sx={{
          p: 0.75,
          px: 1.5,
          borderRadius: "16px",
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          gap: 0.75,
          alignItems: { sm: "center" },
          background: "rgba(255,255,255,0.8)",
          backdropFilter: "blur(12px)",
          border: "1px solid rgba(99,102,241,0.18)",
          transition: "all 0.25s ease",
          "&:focus-within": {
            boxShadow: "0 8px 30px rgba(99,102,241,0.15)",
            border: "1px solid #6366f1",
          },
        }}
      >
        <AutoAwesomeIcon sx={{ color: "#6366f1", fontSize: 22 }} />
        <TextField
          fullWidth
          variant="standard"
          placeholder="Водитель в Кишиневе до 15000 лей…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyPress={(e) => e.key === "Enter" && run()}
          InputProps={{ disableUnderline: true, sx: { fontSize: "1rem" } }}
        />
        <Box
          display="flex"
          alignItems="center"
          gap={0.5}
          sx={{ alignSelf: { xs: "flex-start", sm: "auto" } }}
        >
          <Button
            variant="contained"
            disabled={busy || !query.trim()}
            onClick={() => run()}
            sx={{
              borderRadius: "12px",
              px: 2,
              py: 0.75,
              fontWeight: 600,
              fontSize: "0.85rem",
              minWidth: { xs: "100%", sm: "auto" },
              whiteSpace: "nowrap",
              background: "linear-gradient(135deg, #6366f1, #8b5cf6)",
              "&:hover": { background: "linear-gradient(135deg, #5a5fcf, #7a52d4)" },
            }}
          >
            {busy ? "…" : "Подобрать"}
          </Button>
          {res && (
            <Button size="small" color="inherit" onClick={() => { setRes(null); setQuery(""); clearAiSearchState(); }}>
              <CloseIcon sx={{ fontSize: 20 }} />
            </Button>
          )}
        </Box>
      </Paper>

      {/* Примеры фраз — одной строкой со скроллом на телефоне, перенос на широких */}
      <Stack
        direction="row"
        spacing={1}
        mt={1.25}
        flexWrap={{ xs: "nowrap", sm: "wrap" }}
        useFlexGap
        sx={{ overflowX: { xs: "auto", sm: "visible" }, py: { xs: 0.5 }, "-webkit-overflow-scrolling": "touch" }}
      >
        {examples.map((ex) => (
          <Chip
            key={ex}
            label={ex}
            size="small"
            disabled={busy}
            onClick={() => run(ex)}
            sx={{
              flexShrink: 0,
              borderRadius: "999px",
              bgcolor: "rgba(99,102,241,0.06)",
              color: "text.secondary",
              fontSize: "0.75rem",
              "&:hover": { bgcolor: "rgba(99,102,241,0.12)", color: "#6366f1" },
            }}
          />
        ))}
      </Stack>

      {busy && (
        <Box my={2} display="flex" alignItems="center" gap={1.5} color="text.secondary">
          <CircularProgress size={16} />
          <Typography variant="body2">Спрашиваю нейросеть и подбираю из базы…</Typography>
        </Box>
      )}

      {err && (
        <Typography color="error" variant="body2" my={1.5}>{err}</Typography>
      )}

      {res && (
        <Box mt={2}>
          {/* Чипы «что понял ИИ» */}
          {(chips.length > 0 || used?.needs) && (
            <Stack direction="row" spacing={1} flexWrap="wrap" alignItems="center" useFlexGap mb={1.5}>
              <Typography variant="caption" color="text.secondary" mr={0.5}>
                Понял как:
              </Typography>
              {chips.map((c, i) => (
                <Chip
                  key={i}
                  label={c.label}
                  size="small"
                  sx={{
                    fontSize: "0.75rem",
                    fontWeight: 600,
                    ...(c.cls === "brand"
                      ? { bgcolor: "rgba(99,102,241,0.12)", color: "#6366f1" }
                      : { bgcolor: "rgba(100,116,139,0.1)", color: "text.secondary" }),
                  }}
                />
              ))}
              {used?.needs && (
                <Typography variant="caption" color="text.secondary" fontStyle="italic">
                  {used.needs}
                </Typography>
              )}
            </Stack>
          )}

          {res.total === 0 ? (
            <Paper sx={{ p: 3, textAlign: "center", borderRadius: 2.5, mt: 1 }}>
              <Typography color="text.secondary">
                {res.explanation || "Ничего не нашлось."}
              </Typography>
            </Paper>
          ) : (
            <>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                <Typography variant="body2" fontWeight={600} color="text.secondary">
                  Найдено {res.total}{" "}
                  {res.total === 1 ? "вакансия" : res.total < 5 ? "вакансии" : "вакансий"}
                </Typography>
              </Box>
              <Divider sx={{ mb: 1.5 }} />
              <Stack spacing={0}>
                {res.jobs.map((job, i) => (
                  <Box key={job.id}>
                    <TranslatedJobCard
                      job={job}
                      onMoreClick={(id) => {
                        // сохраняем состояние поиска и позицию скролла, чтобы вернуть при «назад»
                        setAiSearchState(query, res);
                        setAiSearchScroll(window.scrollY);
                        sessionStorage.setItem("lastJobListScrollY", window.scrollY.toString());
                        sessionStorage.setItem("lastJobListJobId", id.toString());
                        navigate(`/job/${id}`);
                      }}
                    />
                    {i !== res.jobs.length - 1 && <Divider sx={{ opacity: 0.3 }} />}
                  </Box>
                ))}
              </Stack>
            </>
          )}
        </Box>
      )}
    </Box>
  );
};

export default AiSearch;